<template>
    <asli-form-section :title="pageTitle">
        <div v-if="currentStep < 3" class="mb-4">
            <p class="mb-4 text-sm">
                Please complete the details below in order to set up your Direct Debit and complete your quote.
            </p>
            <p class="text-sm">
                By setting up a Direct Debit mandate, you are giving An Post Insurance authorisation to instruct your
                bank to transfer money from your bank account to ours.
            </p>
        </div>
        <div v-else class="mb-4 text-sm">
            <p class="mb-4">Please enter your card details below to pay your initial deposit.</p>
            <p class="mb-4">
                For your security, your card provider may also need you to verify your card using your mobile banking
                app or a passcode.
            </p>
            <p class="mb-4">
                If you are having difficulty, you can call us on {{ BRAND_PHONE_NUMBER }} to complete the payment.
            </p>
        </div>
        <div class="py-6">
            <div class="sm:grid sm:gap-6 sm:grid-cols-2">
                <div class="sm:flex border-2 border-green-500 rounded-[3px] p-4 mt-6 sm:mb-6 h-fit order-2 sm:order-1">
                    <direct-debit v-model:currentStep="currentStep" />
                </div>
                <div class="order-1 sm:order-2">
                    <template v-for="section in sections" :key="section.id">
                        <div :class="['mb-4', { 'border-b-2 border-green-500': section.hasBorder }]">
                            <template v-if="section.id === 'creditor'">
                                <img :src="anPostLogo" alt="applied logo" class="w-36 my-5" />
                            </template>

                            <template v-if="section.title">
                                <h3 class="text-lg font-bold mb-4">{{ section.title }}</h3>
                            </template>

                            <div class="text-base mb-[15px]">
                                <div v-for="prop in section.properties" :key="prop.label" class="mb-4">
                                    <p class="font-bold text-xs">{{ prop.label }}</p>
                                    <p class="text-xs">{{ prop.value }}</p>
                                </div>

                                <template v-if="section.id === 'payment'">
                                    <p class="text-xs mb-4">
                                        The day of the month that your direct debit is taken from your account can be
                                        changed once your policy is in force and once your payment above has been
                                        received and cleared.
                                    </p>
                                    <div class="font-bold flex flex-col">
                                        <span class="text-sm">Total payable: </span>
                                        <span class="text-lg">€{{ monthlyValues.totalPayable }}</span>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </asli-form-section>
    <button-bar class="flex-col items-center md:items-end">
        <asli-button id="payment-back" color="secondary" type="button" @click="goBack">Back</asli-button>
    </button-bar>
</template>

<script lang="ts" setup>
import { AsliFormSection, AsliButton, formatToCurrency, isToday } from "@mbs-npm/anpost-ui";
import { ROUTE_NAMES } from "@/router/types";
import { useMainStore } from "@/stores/main";
import { useRouter } from "vue-router";
import ButtonBar from "@/components/ButtonBar.vue";
import { useProposalStore } from "@/stores/risk/risk";
import { computed, onBeforeMount, ref, watch } from "vue";
import { formatDate, formatDateAndTime } from "@/utils/index";
import anPostLogo from "@/assets/anpost-logo-green.png";
import DirectDebit from "./DirectDebit.vue";
import { BRAND_PHONE_NUMBER } from "@/static/constants";
import advancedFormat from "dayjs/plugin/advancedFormat";
import dayjs from "dayjs";
import { goToError } from "@/utils/error-handling";
import { APP_STEP } from "@/stores/types";

dayjs.extend(advancedFormat);

const mainStore = useMainStore();
const proposalStore = useProposalStore();
const router = useRouter();

mainStore.setAppStep(APP_STEP.PAYMENT);

const selectedQuote = computed(() => proposalStore.proposal.quote);

const currentStep = ref(0);
const pageTitle = ref("Direct Debit setup");

const formatCurrency = (value: number | null): string => {
    return value ? formatToCurrency(value.toString(), true) : "0.00";
};

const monthlyValues = computed(() => ({
    deposit: formatCurrency(selectedQuote.value?.monthly_deposit),
    initialPayment: formatCurrency(selectedQuote.value?.monthly_initial_payment),
    payment: formatCurrency(selectedQuote.value?.monthly_payment),
    installments: selectedQuote.value?.monthly_installments ? selectedQuote.value.monthly_installments : 0,
    totalPayable: formatCurrency(selectedQuote.value?.monthly_total_payable),
    totalPayableMinusFee:
        selectedQuote.value?.monthly_total_payable && selectedQuote.value?.monthly_interest
            ? formatCurrency(selectedQuote.value?.monthly_total_payable - selectedQuote.value?.monthly_interest)
            : formatCurrency(selectedQuote.value?.monthly_total_payable),
    interestCost: formatCurrency(selectedQuote.value?.monthly_interest),
    interestPercent: selectedQuote.value?.monthly_interest_percent,
    paymentDue: dayjs(selectedQuote.value.cover_effective_from).format("Do")
}));

watch(currentStep, newVal => {
    if (newVal === 3) {
        pageTitle.value = "Pay your deposit";
    }
});

onBeforeMount(async () => {
    try {
        if (mainStore.fromPaymentFailed) {
            currentStep.value = 3;
            mainStore.setFromPaymentFailed(false);
        }
        mainStore.setLoadingStatePopupText("Preparing direct debit...");
        mainStore.setLoadingState(true);
        await proposalStore.getPaymentPageURL();
    } catch {
        goToError(proposalStore.proposal.uuid as string);
    } finally {
        mainStore.setLoadingState(false);
    }
});

const sections = computed(() => {
    if (!selectedQuote.value) return [];

    return [
        {
            id: "payment",
            title: "Payment Details",
            hasBorder: true,
            properties: [
                {
                    label: "Total amount (before PP Service Fee):",
                    value: `€${monthlyValues.value.totalPayableMinusFee}`
                },
                {
                    label: "Service fee:",
                    value: `${monthlyValues.value.interestPercent}%`
                },
                {
                    label: "Cost of service:",
                    value: `€${monthlyValues.value.interestCost}`
                },
                {
                    label: "First payment now by credit/debit card:",
                    value: `€${monthlyValues.value.deposit}`
                },
                {
                    label: "Subsequent recurring direct debit payments:",
                    value: `${monthlyValues.value.installments} payments of €${monthlyValues.value.payment} due on or around the ${monthlyValues.value.paymentDue} of each month`
                }
            ]
        },
        {
            id: "creditor",
            hasBorder: true,
            properties: [
                {
                    label: "Creditor Name:",
                    value: "One Direct Ireland Ltd. Trading as An Post Insurance"
                },
                {
                    label: "Creditor Postal Address:",
                    value: "Athlone Business Park, Athlone, Co Westmeath, Rep of Ireland, N37 EF40"
                },
                {
                    label: "Creditor Identifier Number:",
                    value: "IE39ZZZ302067"
                },
                {
                    label: "Payment Type:",
                    value: "Recurring"
                }
            ]
        },
        {
            id: "quote",
            title: "Quote Details",
            hasBorder: false,
            properties: [
                {
                    label: "Insurer Details:",
                    value: selectedQuote.value.insurer_name
                },
                {
                    label: "Cover Effective From:",
                    value: isToday(selectedQuote.value.cover_effective_from)
                        ? formatDate(selectedQuote.value.cover_effective_from)
                        : formatDateAndTime(selectedQuote.value.cover_effective_from)
                },
                {
                    label: "Cover Effective To:",
                    value: formatDateAndTime(selectedQuote.value.cover_effective_to)
                }
            ]
        }
    ];
});

function goBack() {
    router.push({
        name: ROUTE_NAMES.Marketing,
        query: {
            uuid: proposalStore.proposal.uuid
        }
    });
}
</script>
